// en_cctv.ts
// Translations for the CCTV module (CCTV.Host.tsx, CCTV.Dialogs.tsx).

export const enCctv = {
  errors: {
    cameraName: "Camera name is required.",
    cameraCode: "Camera code may contain letters, numbers, dots, underscores and dashes.",
    cameraConflict: "A camera with this code already exists for the property.",
    cameraMissing: "Camera not found for this property.",
    cameraUnavailable: "Camera is unavailable for this property.",
    guestMask: "Add a privacy mask for the guest area or explicitly acknowledge the unmasked view.",
    masks: "Privacy masks must be a list of valid image areas.",
    maskRange: "Mask {{number}}: {{field}} must be a percentage between 0 and 100.",
    retentionRange: "{{field}} must be a whole number of days between 1 and 3650.",
    retentionOrder: "Incident retention must be at least as long as standard retention.",
    incidentTitle: "Incident title is required.",
    incidentMissing: "Incident not found for this property.",
    incidentEvents: "One or more events do not belong to this property.",
    eventAcknowledged: "Event not found or already acknowledged.",
    recordingMissing: "Recording not found for this property.",
    timelineRange: "The end of the period must be after its start.",
    request: "CCTV request failed ({{status}}).",
    unknown: "An unexpected error occurred. Try again.",
    network: "Could not connect to the server. Check your connection and try again.",
  },
  errorField: {
    retentionDays: "Retention",
    standardRetentionDays: "Standard retention",
    incidentRetentionDays: "Incident retention",
    x: "Horizontal position",
    y: "Vertical position",
    width: "Width",
    height: "Height",
  },
  auditAction: {
    "camera.created": "Camera created",
    "camera.updated": "Camera updated",
    "event.recorded": "Event recorded",
    "event.acknowledged": "Event acknowledged",
    "incident.created": "Incident created",
    "incident.updated": "Incident updated",
    "recording.playback": "Recording played",
    "recording.export": "Recording exported",
    "retention_policy.updated": "Retention policy updated",
  },
  auditEntity: {
    camera: "Camera",
    event: "Event",
    incident: "Incident",
    recording: "Recording",
    retention_policy: "Retention policy",
  },
  zone: {
    guest: "Guest area",
    staff: "Staff",
    back_of_house: "Back of house",
    outdoor: "Outdoor",
  },
  status: {
    online: "Live",
    offline: "Offline",
    alert: "Alarm",
    disabled: "Disabled",
  },
  recordingMode: {
    continuous: "Continuous recording",
    event: "Event-triggered",
    disabled: "No recording",
  },
  source: {
    motion: "Motion",
    ai: "AI detection",
    access: "Access",
    system: "System",
  },
  incidentStatus: {
    open: "Open",
    investigating: "Investigating",
    closed: "Closed",
  },
  severity: {
    low: "Low",
    medium: "Medium",
    high: "High",
    critical: "Critical",
    info: "Information",
    warning: "Warning",
  },
  incidentType: {
    security: "Security",
    theft: "Theft",
    damage: "Property damage",
    noise: "Noise disturbance",
    maintenance: "Technical",
    operations: "Operations",
    other: "Other",
  },
  common: {
    loadFailed: "Data could not be loaded",
    retry: "Try again",
    loading: "Loading …",
    missingPermission: "Missing permission: {{key}}",
    hotelListUnavailable: "Could not load the hotel list.",
    refresh: "Refresh",
    acknowledge: "Acknowledge",
    days: "{{count}} days",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_many: "{{count}} days",
    days_other: "{{count}} days",
    never: "never",
    unknown: "unknown",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    discard: "Discard",
    none: "—",
  },
  nav: {
    live: "Live wall",
    cameras: "Cameras",
    recordings: "Recordings",
    incidents: "Incidents",
    events: "Events",
    access: "Access",
    privacy: "Privacy",
    health: "System health",
  },
  layout: {
    title: "Security Control Center",
    camerasOnline: "{{online}} of {{total}} cameras online",
    openIncidents: "{{count}} open incidents",
    createIncident: "Create incident",
    hotelListError: "The hotel list could not be loaded",
    resolvingHotel: "Resolving hotel …",
    property: "Property",
  },
  tile: {
    recording: "REC",
    noSignal: "No signal",
    lastImage: "Last image {{time}}",
    masked: "Masked: {{label}}",
    maskedCount: "{{count}} masked",
    alerts: "{{count}} alerts",
    recheck: "Re-check",
    open: "Open full view of {{name}}",
  },
  fullView: {
    subtitle: "{{code}} · {{location}}",
    noStream: "Live video is provided by the {{adapter}} adapter. No stream is connected to this browser view.",
    lastSeen: "Last signal",
    zone: "Zone",
    recording: "Recording",
    retention: "Retention",
    masks: "Privacy masks",
    status: "Status",
    openAlerts: "Open alerts",
  },
  live: {
    subtitle: "{{property}} · What is happening now and what needs attention.",
    noKpis: "No key figures available",
    camerasOnline: "Cameras online",
    allReachable: "All reachable",
    withoutSignal: "{{count}} without signal",
    openIncidents: "Open incidents",
    criticalCount: "{{count}} critical",
    noneCritical: "None critical",
    aiEventsToday: "AI events today",
    ofEventsTotal: "of {{count}} events in total",
    retention: "Retention",
    retentionDefault: "Not set yet · default value",
    incidentRetention: "Incidents {{count}} days",
    noCameras: "No cameras set up yet",
    noCamerasHint: "Add the first camera for this property under “Cameras”.",
    attention: "Needs attention",
    attentionSummary: "{{incidents}} open incidents · {{alerts}} unacknowledged alerts",
    nothingOpen: "Nothing open",
    nothingOpenHint: "No open incidents and all alerts are acknowledged.",
    incidentOpened: "{{reference}} · opened {{time}}",
  },
  cameras: {
    subtitle: "{{property}} · Location, zone, recording mode and retention.",
    add: "Add camera",
    emptyHint: "Create the first camera so the live wall has something to show.",
    colCamera: "Camera",
    colLocation: "Location",
    colZone: "Zone",
    colStatus: "Status",
    colRecording: "Recording",
    colRetention: "Retention",
    colPrivacy: "Privacy",
    colActions: "Actions",
    zonesMasked: "{{count}} zones masked",
    guestAreaUnmasked: "Guest area without mask",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
  },
  recordings: {
    subtitle: "{{property}} · Playback in evidence mode — every access is logged.",
    camera: "Camera",
    density: "Event density",
    densityHint: "Last 24 hours — nobody has to scrub through empty hours.",
    noEvents: "No events in this period",
    noEventsHint: "No events were recorded for this camera in the last 24 hours.",
    bucketTitle: "{{time}} · {{count}} events",
    bucketCritical: ", {{count}} of them critical",
    clips: "Clips",
    clipCount: "{{count}} recordings",
    none: "No recordings",
    noneHint: "There are no stored clips for this camera.",
    colStart: "Start",
    colDuration: "Duration",
    colIncident: "Incident",
    linked: "linked",
    evidence: "Evidence",
    normal: "Normal",
    play: "Play",
    export: "Export",
  },
  incidents: {
    subtitle: "{{property}} · Sequence from camera, locking system and system on one timeline.",
    list: "Incident list",
    entries: "{{count}} entries",
    none: "No incidents",
    noneHint: "No incident has been created for this property yet.",
    eventCount: "{{count}} events",
    sequence: "Sequence",
    pickOne: "Select an incident on the left.",
    noneSelected: "No incident selected",
    noneSelectedHint: "The timeline appears once an incident is selected on the left.",
    laneCamera: "Camera",
    laneAi: "AI",
    laneAccess: "Access",
    laneSystem: "System",
    noTimelineEvents: "No events in the time window",
    noTimelineEventsHint: "No camera or access events have been recorded for this incident yet.",
    timelineFoot: "Linked events are highlighted; the others only fall into the same time window.",
  },
  events: {
    subtitle: "{{property}} · Motion, AI detection and system messages.",
    open: "Open",
    all: "All",
    none: "No events",
    noneHint: "No events have been recorded for this property yet.",
    nothingOpenHint: "All alerts are acknowledged.",
    colTime: "Time",
    colSource: "Source",
    colEvent: "Event",
    colConfidence: "Confidence",
    colAction: "Action",
    measured: "measured",
    acknowledged: "Acknowledged",
  },
  access: {
    subtitle: "{{property}} · Events reported by Keycoder through the ingest interface.",
    none: "No access events",
    noneHint: "As soon as Keycoder sends events to POST /api/hotels/:id/cctv/events, they appear here and on the incident timeline.",
  },
  privacy: {
    subtitle: "{{property}} · Retention periods, masking and access log.",
    noPolicyTitle: "No retention period set yet",
    noPolicyText: "The default of {{count}} days applies. As long as nothing is saved there is no documented decision — not enough for a works agreement or a GDPR Art. 15 request.",
    retention: "Retention",
    retentionHint: "Applies to all cameras without their own period.",
    noPolicy: "Policy could not be loaded",
    standardDays: "Standard (days)",
    incidentDays: "Incident-bound (days)",
    saveFailed: "Saving failed.",
    lastChanged: "Last changed: {{value}}",
    masking: "Masking",
    maskingHint: "Cameras in guest areas without a mask.",
    openCount: "{{count}} open",
    noOpenItems: "No open items",
    noOpenItemsHint: "Every camera in a guest area has at least one masked zone.",
    withoutMask: "No mask",
    auditLog: "Access log",
    auditLogHint: "Playback, export and changes — tamper-proof.",
    noAudit: "No entries yet",
    noAuditHint: "As soon as someone views or exports footage, it appears here.",
    colAction: "Action",
    colObject: "Object",
    colPerson: "Person",
  },
  health: {
    subtitle: "{{property}} · Adapters, reachable cameras and storage.",
    recheck: "Check again",
    none: "No system health available",
    adapters: "Adapters",
    adaptersHint: "Provider-neutral, per connection.",
    noAdapters: "No cameras connected.",
    onlineOf: "{{online}} / {{total}} online",
    complete: "Complete",
    partial: "Partial",
    storage: "Storage",
    storageHint: "Recorded material.",
    clips: "Clips",
    totalDuration: "Total duration",
    evidenceLocked: "Locked as evidence",
    staleTitle: "Cameras without a current signal",
    staleHint: "Nothing received for more than 15 minutes.",
    allReporting: "All cameras are reporting",
    allReportingHint: "No device has been silent for more than 15 minutes.",
    neverSeen: "never seen",
  },
  dialogs: {
    busySaving: "Saving …",
    busyCreating: "Creating …",
    editCamera: "Edit camera · {{code}}",
    addCamera: "Add camera",
    editCameraHint: "Changes are recorded in the access log.",
    addCameraHint: "Code and name are required.",
    saveChanges: "Save changes",
    createCamera: "Create camera",
    name: "Name",
    namePlaceholder: "Corridor floor 3",
    code: "Code",
    location: "Location",
    locationPlaceholder: "Floor 3, east wing",
    zone: "Zone",
    recordingMode: "Recording mode",
    retentionDays: "Retention (days)",
    maskedZones: "Masked zones · values in percent of the image",
    noMaskedZone: "No zone masked.",
    maskLabel: "Label",
    maskLabelPlaceholder: "Sun loungers",
    remove: "Remove",
    addZone: "Add zone",
    maskZoneLabel: "Zone {{count}}",
    unmaskedAck: "This camera films a guest area without a masked zone. This is a deliberate decision that is recorded in the log.",
    saveFailed: "Saving failed.",
    createFailed: "Creation failed.",
    createIncident: "Create incident",
    createIncidentHint: "The incident number is assigned automatically.",
    title: "Title",
    titlePlaceholder: "Repeated access attempt room 314",
    type: "Type",
    severity: "Severity",
    status: "Status",
    camera: "Camera",
    noCamera: "No camera",
    accessLog: "Access log reference",
    assignedTo: "Assigned to",
    assignedToPlaceholder: "Night shift",
    description: "Description",
    descriptionPlaceholder: "What happened, what has already been done?",
  },
};

export default enCctv;
